import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppRoutes } from "../../AppRoutes";
import { EditOutlined, CloseOutlined, UndoOutlined } from "@ant-design/icons";
import { Space, Popconfirm, Form } from "antd";
import {
  appTitle,
  dateFormat,
  Departement,
  MonthsPortuguese,
  FollowUpEventStatus,
} from "../common/GeneralConfigurations";
import moment from "moment";
import { EmployeeFollowUpEventMonthlyListForm } from "./EmployeeFollowUpEventMonthlyListForm";
import {
  checkPreviousUnresolvedEmployeeFollowUpEventsById,
  getEmployeeFollowUpEventsByDate,
  getEmployeeFollowUpEventsByDateAndStatusCounter,
  getEmployeeFollowUpEventById,
  updateEmployeeFollowUpEvent,
  notifyAssignPeople,
} from "../../api/EmployeeFollowUpEventApi";
import { getFollowUpEventList } from "../../api/FollowUpEventApi";
import { GetUserByDepartmentId } from "../../api/UserApi";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import { getAuthenticatedUserId } from "../../api/ApiUtils";
import { getContractTypes } from "../../api/EmployeeApi";

export const ManageEmployeeFollowUpEventMonthlyListPage = () => {
  //CONSTS
  const date = moment(moment(), dateFormat);
  const month = date.month() + 1;
  const year = date.year();
  const previousDate = moment(moment(), dateFormat).subtract(1, "M");
  const previousMonth = previousDate.month() + 1;
  const previousYear = previousDate.year();
  const nextDate = moment(moment(), dateFormat).add(1, "M");
  const nextMonth = nextDate.month() + 1;
  const nextYear = nextDate.year();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const title = t("followUpEvent.title");

  //COLUMNS
  const columns = [
    {
      title: t("followUpEvent.columns.consultant"),
      dataIndex: "employeeName",
      sorter: true,
      width: "15%",
      render: (text, record) => (
        <Link
          to={AppRoutes.consultantById.createPath(record.employeeId)}
          target="_blank"
        >
          {FirstLastName(text)}
        </Link>
      ),
    },
    {
      title: t("followUpEvent.columns.reference"),
      dataIndex: "referenceDate",
      render: (text) =>
        text
          ? MonthsPortuguese[parseInt(moment(text).format("MM"))] +
            " " +
            moment(text).format("YYYY")
          : undefined,
      width: "7%",
    },
    {
      title: t("followUpEvent.columns.date"),
      dataIndex: "date",
      width: "7%",
      sorter: true,
      render: (text, record) =>
        record.date ? moment(record.date).format(dateFormat) : <></>,
    },
    {
      title: t("followUpEvent.columns.description"),
      dataIndex: "followUpEventName",
      width: "30%",
    },
    {
      title: t("followUpEvent.columns.manager"),
      dataIndex: "managerName",
      sorter: true,
      width: "12%",
      render: (text) =>
        text ? <div>{FirstLastName(text)}</div> : <b>NO MANAGER</b>,
    },
    {
      title: t("followUpEvent.columns.assignedTo"),
      dataIndex: "doneByName",
      sorter: true,
      render: (text, record) => (
        <>
          {record.status === 0 || record.status === 1 ? (
            <AdnDropdown
              name="doneByName"
              placeholder={t("followUpEvent.placeholders.assignPerson")}
              handleChange={(value) => {
                handleAssignPerson(
                  record.id,
                  record.employeeName,
                  record.employeeId,
                  value
                );
              }}
              defaultValue={text}
              allowClear={true}
              options={assignedToList}
            />
          ) : (
            <>{text}</>
          )}
        </>
      ),
      width: "12%",
    },
    {
      title: t("followUpEvent.columns.status"),
      dataIndex: "status",
      sorter: true,
      width: "10%",
      render: (text, record) =>
        record.status == 0 ? (
          <div className="state-tag tag-to-do">Not Assigned</div>
        ) : record.status == 1 ? (
          <div className="state-tag tag-danger">Not Done</div>
        ) : record.status === 2 ? (
          <div className="state-tag tag-done">Done</div>
        ) : (
          <div className="state-tag tag-canceled">Canceled</div>
        ),
    },
    {
      title: t("followUpEvent.columns.actions"),
      dataIndex: "actions",
      render: (text, record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          {record.status === 0 ? (
            <></>
          ) : (
            <EditOutlined
              style={{ color: "black", fontSize: "large" }}
              onClick={() => {
                saveLocalVariables();
                navigate(AppRoutes.followUpEventById.createPath(record.id));
              }}
            />
          )}

          {record.status === 0 || record.status === 1 ? (
            <>
              <CloseOutlined
                style={{ color: "red" }}
                onClick={() => handleCancelEventModal(record)}
              />
            </>
          ) : record.status === 3 || record.status === 2 ? (
            <Popconfirm
              title="Are you sure you want to uncancel this event?"
              onConfirm={() => {
                handleUnCancelEvent(record.id);
              }}
            >
              <UndoOutlined style={{ color: "blue" }} />
            </Popconfirm>
          ) : (
            <></>
          )}
        </Space>
      ),
      width: "7%",
    },
  ];

  // //STATE CONSTS
  const [loading, setLoading] = useState(true);
  const [initValues, setInitValues] = useState({
    id: null,
    employeeId: null,
    followUpEventId: null,
    status: 0,
    date: undefined,
    feedBack: "",
    note: "",
    doneById: null,
    companyLevel: null,
    clientLevel: null,
    projectLevel: null,
    createdBy: null,
    createdAt: "",
    updatedBy: null,
    updatedAt: "",
    employeeName: "",
    eventType: "",
    doneByName: "",
    authenticatedId: '00000000-0000-0000-0000-000000000000',
  });
  const [isUserRH, setIsUserRH] = useState(false);
  const [isUserManager, setIsUserManager] = useState(false);

  const userID = getAuthenticatedUserId();

  //Current Month States
  const [dataCurrent, setDataCurrent] = useState([]);
  const [orderByCurrent, setOrderByCurrent] = useState("");
  const [employeeNameCurrent, setEmployeeNameCurrent] = useState();
  const [assignedToNameCurrent, setAssignedToNameCurrent] = useState();
  const [managerNameCurrent, setManagerNameCurrent] = useState();
  const [eventTypeCurrent, setEventTypeCurrent] = useState("");
  const [contractTypeCurrent, setContractTypeCurrent] = useState("");
  const [statusCurrent, setStatusCurrent] = useState();
  const [currentPageCurrent, setCurrentPageCurrent] = useState(1);
  const [totalRecordsCurrent, setTotalRecordsCurrent] = useState(0);
  const [totalRecordsCurrentBanner, setTotalRecordsCurrentBanner] = useState(0);

  //Previous Month States
  const [dataPrevious, setDataPrevious] = useState([]);
  const [orderByPrevious, setOrderByPrevious] = useState("");
  const [employeeNamePrevious, setEmployeeNamePrevious] = useState();
  const [assignedToNamePrevious, setAssignedToNamePrevious] = useState();
  const [managerNamePrevious, setManagerNamePrevious] = useState();
  const [eventTypePrevious, setEventTypePrevious] = useState("");
  const [contractTypePrevious, setContractTypePrevious] = useState("");
  const [statusPrevious, setStatusPrevious] = useState();
  const [currentPagePrevious, setCurrentPagePrevious] = useState(1);
  const [totalRecordsPrevious, setTotalRecordsPrevious] = useState(0);
  const [totalRecordsPreviousBanner, setTotalRecordsPreviousBanner] =
    useState(0);

  //Next Month States
  const [dataNext, setDataNext] = useState([]);
  const [orderByNext, setOrderByNext] = useState("");
  const [employeeNameNext, setEmployeeNameNext] = useState();
  const [assignedToNameNext, setAssignedToNameNext] = useState();
  const [managerNameNext, setManagerNameNext] = useState();
  const [eventTypeNext, setEventTypeNext] = useState("");
  const [contractTypeNext, setContractTypeNext] = useState("");
  const [statusNext, setStatusNext] = useState();
  const [currentPageNext, setCurrentPageNext] = useState(1);
  const [totalRecordsNext, setTotalRecordsNext] = useState(0);
  const [totalRecordsNextBanner, setTotalRecordsNextBanner] = useState(0);

  const [refetchData, setRefetchData] = useState(false);
  const [cancelEventVisible, setCancelEventVisible] = useState(false);
  const [unresolvedModalVisible, setUnresolvedModalVisible] = useState(false);
  const [unresolvedEmployee, setUnresolvedEmployee] = useState({});
  const [formValues, setFormValues] = useState({});

  const [assignedToList, setAssignedToList] = useState([]);
  const [managersList, setManagersList] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [eventEmployeeId, setEventEmployeeId] = useState(null);
  const [eventAssignedToId, setEventAssignedToId] = useState(null);

  // USE EFFECTS
  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
    fetchFollowUpEventTypes();
    fetchManagersHRList();
    checkLocalVariables();
    fetchgetContractTypes();
  }, []);

  const fetchgetContractTypes = async () => {
    await getContractTypes()
      .then((res) => {
        if (res.status === 200) {
          setContractTypeOptions(res.data);
        } else {
          AdnMessageError("Error loading contract types.");
        }
      })
      .catch(() => {
        AdnMessageError("Error loading contract types.");
      });
  };

  const checkLocalVariables = () => {
    let storageVariablesPrevious = JSON.parse(
      localStorage.getItem("MonthlyEventsFiltersPrevious")
    );
    let storageVariablesCurrent = JSON.parse(
      localStorage.getItem("MonthlyEventsFiltersCurrent")
    );

    let storageVariablesNext = JSON.parse(
      localStorage.getItem("MonthlyEventsFiltersNext")
    );

    if (storageVariablesPrevious !== null) {
      setCurrentPagePrevious(storageVariablesPrevious.currentPage);
      setOrderByPrevious(storageVariablesPrevious.orderBy);
      setEmployeeNamePrevious(storageVariablesPrevious.employeeName);
      setAssignedToNamePrevious(storageVariablesPrevious.assignedToName);
      setManagerNamePrevious(storageVariablesPrevious.managerName);
      setEventTypePrevious(storageVariablesPrevious.eventType);
      setStatusPrevious(storageVariablesPrevious.eventStatus);
      setContractTypePrevious(storageVariablesPrevious.contractType);
    }
    if (storageVariablesCurrent !== null) {
      setCurrentPageCurrent(storageVariablesCurrent.currentPage);
      setOrderByCurrent(storageVariablesCurrent.orderBy);
      setEmployeeNameCurrent(storageVariablesCurrent.employeeName);
      setAssignedToNameCurrent(storageVariablesCurrent.assignedToName);
      setManagerNameCurrent(storageVariablesCurrent.managerName);
      setEventTypeCurrent(storageVariablesCurrent.eventType);
      setStatusCurrent(storageVariablesCurrent.eventStatus);
      setContractTypeCurrent(storageVariablesCurrent.contractType);
    }
    if (storageVariablesNext !== null) {
      setCurrentPageNext(storageVariablesNext.currentPage);
      setOrderByNext(storageVariablesNext.orderBy);
      setEmployeeNameNext(storageVariablesNext.employeeName);
      setAssignedToNameNext(storageVariablesNext.assignedToName);
      setManagerNameNext(storageVariablesNext.managerName);
      setEventTypeNext(storageVariablesNext.eventType);
      setStatusNext(storageVariablesNext.eventStatus);
      setContractTypeNext(storageVariablesNext.contractType);
    }
    setRefetchData(!refetchData);
    clearLocalVariables();
  };

  const saveLocalVariables = () => {
    let localVariablesPrevious = {
      currentPage: currentPagePrevious,
      orderBy: orderByPrevious,
      employeeName: employeeNamePrevious,
      assignedToName: assignedToNamePrevious,
      managerName: managerNamePrevious,
      eventType: eventTypePrevious,
      eventStatus: statusPrevious,
      contractType: contractTypePrevious,
    };
    let localVariablesCurrent = {
      currentPage: currentPageCurrent,
      orderBy: orderByCurrent,
      employeeName: employeeNameCurrent,
      assignedToName: assignedToNameCurrent,
      managerName: managerNameCurrent,
      eventType: eventTypeCurrent,
      eventStatus: statusCurrent,
      contractType: contractTypeCurrent,
    };
    let localVariablesNext = {
      currentPage: currentPageNext,
      orderBy: orderByNext,
      employeeName: employeeNameNext,
      assignedToName: assignedToNameNext,
      managerName: managerNameNext,
      eventType: eventTypeNext,
      eventStatus: statusNext,
      contractType: contractTypeNext,
    };
    localStorage.setItem(
      "MonthlyEventsFiltersPrevious",
      JSON.stringify(localVariablesPrevious)
    );
    localStorage.setItem(
      "MonthlyEventsFiltersCurrent",
      JSON.stringify(localVariablesCurrent)
    );
    localStorage.setItem(
      "MonthlyEventsFiltersNext",
      JSON.stringify(localVariablesNext)
    );
  };

  const clearLocalVariables = () => {
    localStorage.removeItem("MonthlyEventsFiltersPrevious");
    localStorage.removeItem("MonthlyEventsFiltersCurrent");
    localStorage.removeItem("MonthlyEventsFiltersNext");
  };

  useEffect(() => {
    const employeeTimeOut = setTimeout(async () => {
      await fetchCurrentMonthData();
    }, 300);
    return () => {
      clearTimeout(employeeTimeOut);
    };
  }, [
    currentPageCurrent,
    orderByCurrent,
    employeeNameCurrent,
    assignedToNameCurrent,
    managerNameCurrent,
    eventTypeCurrent,
    contractTypeCurrent,
    statusCurrent,
    contractTypeCurrent,
    refetchData,
  ]);

  useEffect(() => {
    const employeeTimeOut = setTimeout(async () => {
      await fetchPreviousMonthData();
    }, 300);
    return () => {
      clearTimeout(employeeTimeOut);
    };
  }, [
    currentPagePrevious,
    orderByPrevious,
    employeeNamePrevious,
    assignedToNamePrevious,
    managerNamePrevious,
    eventTypePrevious,
    contractTypePrevious,
    statusPrevious,
    contractTypePrevious,
    refetchData,
  ]);

  useEffect(() => {
    const employeeTimeOut = setTimeout(async () => {
      await fetchNextMonthData();
    }, 300);
    return () => {
      clearTimeout(employeeTimeOut);
    };
  }, [
    currentPageNext,
    orderByNext,
    employeeNameNext,
    assignedToNameNext,
    managerNameNext,
    eventTypeNext,
    contractTypeNext,
    statusNext,
    contractTypeNext,
    refetchData,
  ]);

  useEffect(() => {
    if (initValues.id != null) {
      const onUpdateFollowUpEvents = async () => {
        await updateEmployeeFollowUpEvent(initValues)
          .then((res) => {
            if (res.status === 200) {
              setRefetchData(!refetchData);
              AdnMessageSuccess("Follow Up Event updated with success.");
            } else if (res && res.data && res.data.errors) {
              res.data.errors.map((e) => {
                AdnMessageError(e.message);
              });
            } else {
              AdnMessageError(
                "Consultant has already started to answer the quiz, cannot unassign a person now, only update."
              );
            }
          })
          .catch(() => {
            AdnMessageError(t("followUpEvent.messages.couldNotUpdate"));
          });
      };

      onUpdateFollowUpEvents();
    }
  }, [initValues]);

  useEffect(() => {
    handleIsUserRH();
    handleIsUserManager();
  }, []);

  //FUNCTIONS
  const FirstLastName = (name) => {
    let fullName = name.split(" ");
    let firstName = fullName[0];
    return firstName.concat(" " + fullName[fullName.length - 1]);
  };

  const handleIsUserRH = async () => {
    const UserId = getAuthenticatedUserId();
    let HRList = [];

    const res = await GetUserByDepartmentId(Departement.HumanResources);
    if (res.status === 200) {
      HRList = res.data.result;
    }

    const hasUserId = HRList.some((item) => item.id == UserId);

    setIsUserRH(hasUserId);
  };

  const handleIsUserManager = async () => {
    const UserId = getAuthenticatedUserId();
    let ManagerList = [];

    const res = await GetUserByDepartmentId(Departement.Manager);
    if (res.status === 200) {
      ManagerList = res.data.result;
    }

    const hasUserId = ManagerList.some((item) => item.id == UserId);

    if (hasUserId) {
      setIsUserManager(true);
    }
  };

  const fetchFollowUpEventTypes = async () => {
    await getFollowUpEventList(1, 50, "", "", "", "False")
      .then((res) => {
        if (res.status === 200) {
          let followUpEventsList = res.data.result;
          let eventList = [];

          followUpEventsList.forEach((elem) => {
            if (eventList.length > 0) {
              let checkUnic = eventList.filter((x) => x.name === elem.name);

              if (checkUnic.length === 0) {
                eventList.push(elem);
              }
            } else {
              eventList.push(elem);
            }
          });

          setEventTypeOptions([...eventList]);
        } else {
          AdnMessageError("Error loading event types.");
        }
      })
      .catch(() => {
        AdnMessageError("Error loading event types.");
      });
  };

  const fetchManagersHRList = async () => {
    let HRArray = [];
    let ManagerArray = [];
    await GetUserByDepartmentId(Departement.Manager)
      .then((res) => {
        if (res.status === 200) {
          const filteredManagers = res.data.result.filter(
            (manager) => !manager.deleted
          );
          setManagersList(filteredManagers);
          ManagerArray = filteredManagers;
        } else {
          AdnMessageError("Error loading managers list.");
        }
      })
      .catch(() => {
        AdnMessageError("Error loading managers list.");
      });
    await GetUserByDepartmentId(Departement.HumanResources)
      .then((res) => {
        if (res.status === 200) {
          HRArray = res.data.result.filter((hr) => !hr.deleted);
        } else {
          AdnMessageError("Error loading HR list.");
        }
      })
      .catch(() => {
        AdnMessageError("Error loading HR list.");
      });
    setAssignedToList(HRArray.concat(ManagerArray));
  };

  const fetchCurrentMonthData = async () => {
    await getEmployeeFollowUpEventsByDate(
      month,
      year,
      orderByCurrent,
      14,
      currentPageCurrent,
      employeeNameCurrent,
      assignedToNameCurrent,
      managerNameCurrent,
      eventTypeCurrent,
      statusCurrent,
      contractTypeCurrent,
      userID
    )
      .then((res) => {
        if (res.status === 200) {
          setDataCurrent(res.data.result);
          setTotalRecordsCurrent(res.data.totalRecords);
        } else {
          AdnMessageError(t("followUpEvent.messages.errorRetrievingCurrent"));
        }
      })
      .catch(() => {
        AdnMessageError(t("followUpEvent.messages.errorRetrievingCurrent"));
      });

    const statusIds = [
      FollowUpEventStatus.NotAssigned,
      FollowUpEventStatus.NotDone,
    ];
    await getEmployeeFollowUpEventsByDateAndStatusCounter(
      month,
      year,
      statusIds
    )
      .then((res) => {
        if (res.status === 200) {
          setTotalRecordsCurrentBanner(res.data);
        } else {
          AdnMessageError(
            t("followUpEvent.messages.errorRetrievingTotalCurrent")
          );
        }
      })
      .catch(() => {
        AdnMessageError(
          t("followUpEvent.messages.errorRetrievingTotalCurrent")
        );
      });
    setLoading(false);
  };

  const fetchPreviousMonthData = async () => {
    await getEmployeeFollowUpEventsByDate(
      previousMonth,
      previousYear,
      orderByPrevious,
      14,
      currentPagePrevious,
      employeeNamePrevious,
      assignedToNamePrevious,
      managerNamePrevious,
      eventTypePrevious,
      statusPrevious,
      contractTypePrevious,
      userID
    )
      .then((res) => {
        if (res.status === 200) {
          setDataPrevious(res.data.result);
          setTotalRecordsPrevious(res.data.totalRecords);
        } else {
          AdnMessageError(t("followUpEvent.messages.errorRetrievingPrevious"));
        }
      })
      .catch(() => {
        AdnMessageError(t("followUpEvent.messages.errorRetrievingPrevious"));
      });

    const statusIds = [
      FollowUpEventStatus.NotAssigned,
      FollowUpEventStatus.NotDone,
    ];
    await getEmployeeFollowUpEventsByDateAndStatusCounter(
      previousMonth,
      previousYear,
      statusIds
    )
      .then((res) => {
        if (res.status === 200) {
          setTotalRecordsPreviousBanner(res.data);
        } else {
          AdnMessageError("Error retrieving total previous Follow Up Events");
        }
      })
      .catch(() => {
        AdnMessageError("Error retrieving total previous Follow Up Events");
      });
  };

  const fetchNextMonthData = async () => {
    await getEmployeeFollowUpEventsByDate(
      nextMonth,
      nextYear,
      orderByNext,
      14,
      currentPageNext,
      employeeNameNext,
      assignedToNameNext,
      managerNameNext,
      eventTypeNext,
      statusNext,
      contractTypeNext,
      userID
    )
      .then((res) => {
        if (res.status === 200) {
          setDataNext(res.data.result);
          setTotalRecordsNext(res.data.totalRecords);
        } else {
          AdnMessageError("Error retrieving next Follow Up Events");
        }
      })
      .catch(() => {
        AdnMessageError("Error retrieving next Follow Up Events");
      });

    const statusIds = [
      FollowUpEventStatus.NotAssigned,
      FollowUpEventStatus.NotDone,
    ];
    await getEmployeeFollowUpEventsByDateAndStatusCounter(
      nextMonth,
      nextYear,
      statusIds
    )
      .then((res) => {
        if (res.status === 200) {
          setTotalRecordsNextBanner(res.data);
        } else {
          AdnMessageError("Error retrieving total next Follow Up Events");
        }
      })
      .catch(() => {
        AdnMessageError("Error retrieving total next Follow Up Events");
      });
  };

  const handleCancelEventModal = (values) => {
    setFormValues(values);
    setCancelEventVisible(true);
  };

  const handleCancelEventFinish = (values) => {
    form.resetFields();
    getEmployeeFollowUpEventById(formValues.id)
      .then((res) => {
        if (res.status === 200) {
          res.data.status = 3;
          res.data.note = values.note;
          setInitValues(res.data);
        } else {
          AdnMessageError("Error canceling event.");
        }
      })
      .catch(() => {
        AdnMessageError("Error canceling event.");
      });
    setCancelEventVisible(false);
  };

  const handleUnCancelEvent = (employeeFollowUpEventId) => {
    getEmployeeFollowUpEventById(employeeFollowUpEventId)
      .then((res) => {
        if (res.status === 200) {
          res.data.status = 0;
          res.data.doneById = eventAssignedToId ? eventAssignedToId : null;
          res.data.date = null;
          setInitValues(res.data);
        } else {
          AdnMessageError("Error un-canceling event.");
        }
      })
      .catch(() => {
        AdnMessageError("Error un-canceling event.");
      });
  };

  const handleOrderListCurrent = (pag, filter, sorter) => {
    sorter.order === "descend"
      ? setOrderByCurrent("-" + sorter.field)
      : sorter.order === "ascend"
      ? setOrderByCurrent(sorter.field)
      : setOrderByCurrent("");
  };

  const handleOrderListPrevious = (pag, filter, sorter) => {
    sorter.order === "descend"
      ? setOrderByPrevious("-" + sorter.field)
      : sorter.order === "ascend"
      ? setOrderByPrevious(sorter.field)
      : setOrderByPrevious("");
  };

  const handleOrderListNext = (pag, filter, sorter) => {
    sorter.order === "descend"
      ? setOrderByNext("-" + sorter.field)
      : sorter.order === "ascend"
      ? setOrderByNext(sorter.field)
      : setOrderByNext("");
  };

  const handleAssignedToChange = (value, panel) => {
    switch (panel) {
      case "previous":
        if (value === undefined) return setAssignedToNamePrevious("");
        setAssignedToNamePrevious(value);
        break;
      case "current":
        if (value === undefined) return setAssignedToNameCurrent("");
        setAssignedToNameCurrent(value);
        break;
      case "next":
        if (value === undefined) return setAssignedToNameNext("");
        setAssignedToNameNext(value);
        break;
      default:
        break;
    }
  };

  const handleManagerChange = (value, panel) => {
    switch (panel) {
      case "previous":
        if (value === undefined) return setManagerNamePrevious("");
        setManagerNamePrevious(value);
        break;
      case "current":
        if (value === undefined) return setManagerNameCurrent("");
        setManagerNameCurrent(value);
        break;
      case "next":
        if (value === undefined) return setManagerNameNext("");
        setManagerNameNext(value);
        break;
      default:
        break;
    }
  };

  const handleEventTypeChange = (value, panel) => {
    let val = "";

    if (value !== undefined) {
      val = eventTypeOptions.filter((x) => x.id === value)[0].name;
    }

    switch (panel) {
      case "previous":
        setEventTypePrevious(val);
        break;
      case "current":
        setEventTypeCurrent(val);
        break;
      case "next":
        setEventTypeNext(val);
        break;
      default:
        break;
    }
  };
  const handleContractTypeChange = (value, panel) => {
    let val = "";

    if (value !== undefined) {
      val = value;
    }

    switch (panel) {
      case "previous":
        setContractTypePrevious(val);
        break;
      case "current":
        setContractTypeCurrent(val);
        break;
      case "next":
        setContractTypeNext(val);
        break;
      default:
        break;
    }
  };

  const handleEventStatusChange = (value, panel) => {
    switch (panel) {
      case "previous":
        if (value === undefined) return setStatusPrevious("");
        setStatusPrevious(value);
        break;
      case "current":
        if (value === undefined) return setStatusCurrent("");
        setStatusCurrent(value);
        break;
      case "next":
        if (value === undefined) return setStatusNext("");
        setStatusNext(value);
        break;
      default:
        break;
    }
  };

  const handleEmployeeSearchChange = (value, panel) => {
    switch (panel) {
      case "previous":
        if (value === undefined && value?.length < 2)
          return setEmployeeNamePrevious("");
        setEmployeeNamePrevious(value);
        break;
      case "current":
        if (value === undefined && value?.length < 2)
          return setEmployeeNameCurrent("");
        setEmployeeNameCurrent(value);
        break;
      case "next":
        if (value === undefined && value?.length < 2)
          return setEmployeeNameNext("");
        setEmployeeNameNext(value);
        break;
      default:
        break;
    }
  };

  const handleAssignPerson = async (
    employeeFollowUpEventId,
    employeeName,
    employeeId,
    assignedToId
  ) => {
    setEventEmployeeId(employeeFollowUpEventId);
    setEventAssignedToId(assignedToId);
    await checkPreviousUnresolvedEmployeeFollowUpEventsById(
      employeeFollowUpEventId
    ).then(async (res) => {
      if (res.status === 200) {
        if (res.data && res.data.listOfEvents.length > 0) {
          setUnresolvedEmployee({
            employeeId: employeeId,
            employeeName: employeeName,
            listOfEvents: res.data.listOfEvents,
          });
          setUnresolvedModalVisible(true);
        } else {
          confirmAssignPerson(employeeFollowUpEventId, assignedToId);
        }
      } else {
        AdnMessageError("Could not assign person");
      }
    });
  };

  const confirmAssignPerson = async (employeeFollowUpEventId, assignedToId) => {
    await getEmployeeFollowUpEventById(employeeFollowUpEventId)
      .then((res) => {
        if (res.status === 200) {
          res.data.doneById = assignedToId;
          if (assignedToId != null) {
            res.data.status = 1;
          } else {
            res.data.status = 0;
            }
          setInitValues(res.data);
        } else {
          AdnMessageError("Could not confirm assign person");
        }
      })
      .catch((ex) => {
        AdnMessageError("Could not confirm assign person");
      });
  };

  const handleNotifyAssignedPeople = () => {
    notifyAssignPeople()
      .then((res) => {
        if (res.status === 200) {
          AdnMessageSuccess(
            res.data.assignedPeople + " people have been notified."
          );
        } else if (res && res.data && res.data.errors) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError("Could not notify the people.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not notify the people.");
      });
  };

  return (
    <EmployeeFollowUpEventMonthlyListForm
      title={title}
      loading={loading}
      columns={columns}
      dataPrevious={dataPrevious}
      dataCurrent={dataCurrent}
      dataNext={dataNext}
      currentPageCurrent={currentPageCurrent}
      currentPageNext={currentPageNext}
      currentPagePrevious={currentPagePrevious}
      handleChangePageCurrent={setCurrentPageCurrent}
      handleChangePageNext={setCurrentPageNext}
      handleChangePagePrevious={setCurrentPagePrevious}
      totalRecordsCurrent={totalRecordsCurrent}
      totalRecordsNext={totalRecordsNext}
      totalRecordsPrevious={totalRecordsPrevious}
      totalRecordsCurrentBanner={totalRecordsCurrentBanner}
      totalRecordsPreviousBanner={totalRecordsPreviousBanner}
      totalRecordsNextBanner={totalRecordsNextBanner}
      handleOrderListCurrent={handleOrderListCurrent}
      handleOrderListNext={handleOrderListNext}
      handleOrderListPrevious={handleOrderListPrevious}
      handleEventStatusChange={handleEventStatusChange}
      handleEmployeeSearchChange={handleEmployeeSearchChange}
      handleAssignedToChange={handleAssignedToChange}
      handleManagerChange={handleManagerChange}
      handleEventTypeChange={handleEventTypeChange}
      assignedToList={assignedToList}
      managersList={managersList}
      eventTypeOptions={eventTypeOptions}
      contractTypeOptions={contractTypeOptions}
      form={form}
      cancelEventVisible={cancelEventVisible}
      setCancelEventVisible={setCancelEventVisible}
      handleCancelEventFinish={handleCancelEventFinish}
      formValues={formValues}
      unresolvedModalVisible={unresolvedModalVisible}
      setUnresolvedModalVisible={setUnresolvedModalVisible}
      unresolvedEmployee={unresolvedEmployee}
      confirmAssignPerson={confirmAssignPerson}
      eventEmployeeId={eventEmployeeId}
      eventAssignedToId={eventAssignedToId}
      employeeNamePrevious={employeeNamePrevious}
      assignedToNamePrevious={assignedToNamePrevious}
      managerNamePrevious={managerNamePrevious}
      eventTypePrevious={eventTypePrevious}
      statusPrevious={statusPrevious}
      employeeNameCurrent={employeeNameCurrent}
      assignedToNameCurrent={assignedToNameCurrent}
      managerNameCurrent={managerNameCurrent}
      eventTypeCurrent={eventTypeCurrent}
      statusCurrent={statusCurrent}
      employeeNameNext={employeeNameNext}
      assignedToNameNext={assignedToNameNext}
      managerNameNext={managerNameNext}
      eventTypeNext={eventTypeNext}
      statusNext={statusNext}
      isUserRH={isUserRH}
      isUserManager={isUserManager}
      handleNotifyAssignedPeople={handleNotifyAssignedPeople}
      setDataCurrent={setDataCurrent}
      handleContractTypeChange={handleContractTypeChange}
      contractTypePrevious={contractTypePrevious}
      contractTypeCurrent={contractTypeCurrent}
      contractTypeNext={contractTypeNext}
    />
  );
};
